import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import { Button, Heading } from '@paro.io/base-ui'

import { FURLOUGH_CODE_OPTIONS } from '../../../shared/constants/paroReferrals'
import { useQueryParams } from '../../../shared/customHooks/useQueryParams'
import ApplicationInfoModal from './ApplicationInfoModal'
import Business from './Business'
import CurrentResident from './CurrentResident'
import Header from './Header'
import { calcPasswordStrength, checkDuplicateEmail, validationSchema } from './service'
import User from './User'
import TermsOfUse from './TermsOfUse'
import Benefits from './Benefits'
import LinkedInCreateAccount from './LinkedInCreateAccount'

interface CreateAccountProps {
  history: any
  isFurloughReferral: boolean
}
declare var window: any

const CreateAccount = ({ history, isFurloughReferral }: CreateAccountProps) => {
  const query = useQueryParams()
  const [isCurrentResident, setIsCurrentResident] = useState<boolean | null>(true)
  const [isLoading, setIsLoading] = useState(false)
  const furloughSource = query.get('source')?.toLowerCase()
  const isLinkedInAuthEnabled = process.env.REACT_APP_LINKEDIN_AUTH_ENABLED === 'true'

  const isCustomReferral =
    furloughSource === 'join-paro-becker' ||
    furloughSource === 'join-paro-ladders' ||
    furloughSource === 'join-paro-cpa-directory' ||
    furloughSource === 'mycpe' ||
    furloughSource === 'thedailycpa' ||
    furloughSource === 'join-paro-cfo-dive' ||
    furloughSource === 'join-paro-american-alliance-mergers-acquisitions' ||
    furloughSource === 'join-paro-accounting-today'

  const getFurloughCode = () => {
    switch (furloughSource) {
      case 'join-paro-becker':
        return 'Becker'
      case 'join-paro-ladders':
        return 'Ladders'
      case 'join-paro-cpa-directory':
        return 'CPADirectory'
      case 'join-paro-going-concern':
        return 'Going Concern'
      case 'mycpe':
        return 'myCPE'
      case 'thedailycpa':
        return 'The Daily CPA'
      case 'cpethink':
        return 'CPEThink'
      case 'join-paro-cfo-dive':
        return 'CFO Dive'
      case 'join-paro-american-alliance-mergers-acquisitions':
        return 'American Alliance For Mergers and Acquisitions'
      case 'join-paro-accounting-today':
        return 'Accounting Today'
      case 'join-paro-accounting-today-cas-summit-2022':
        return 'Accounting Today CAS Summit 2022'
      default:
        return ''
    }
  }

  const furloughCode = getFurloughCode()

  return (
    <Formik
      validateOnBlur={false}
      initialValues={{
        acceptedTerms: true,
        companyName: '',
        confirmPassword: '',
        email: '',
        firstName: '',
        furloughCode,
        isCompany: false,
        isFurloughReferral: isFurloughReferral,
        lastName: '',
        password: '',
        registrationSource: furloughSource,
      }}
      validationSchema={validationSchema}
      isInitialValid={false}
      onSubmit={async (values, actions) => {
        setIsLoading(true)
        const { firstName, lastName, email, password, confirmPassword, companyName, furloughCode, registrationSource } = values
        const matchingFurloughCodeOption = FURLOUGH_CODE_OPTIONS.find(
          (furloughCodeOption) => furloughCodeOption.label === furloughCode,
        )
        window?.heap?.identify(email)

        if (password !== confirmPassword) {
          actions.setFieldError('password', 'Passwords must match')
          actions.setFieldError('confirmPassword', 'Passwords must match')
          actions.setSubmitting(false)
          setIsLoading(false)
          return
        }

        const { valid, message } = await checkDuplicateEmail(email)

        if (!valid) {
          actions.setFieldError('email', message)
          actions.setSubmitting(false)
          setIsLoading(false)
          return
        }

        const payload = {
          freelancer: {
            firstName,
            lastName,
            email,
            password,
            companyName,
            furloughCode: matchingFurloughCodeOption?.value || '',
            registrationSource,
          },
        }

        axios
          .post(`${process.env.REACT_APP_V3_ENDPOINT}/expert-profiles/expert-user`, payload)
          .then((response) => {
            if (response.status === 200) {
              setIsLoading(false)
              history.push(`/registration/verify/${email}`)
            }
          })
          .catch((error) => {
            console.log('Error creating user', error)
            const context = {
              variables: JSON.stringify(payload),
              path: `${process.env.REACT_APP_V3_ENDPOINT}/expert-profiles/expert-user`,
            }
            Sentry.captureException(`Error creating user: ${error}`, {
              contexts: { context },
            })
          })
      }}
    >
      {(props) => {
        const {
          isSubmitting,
          isValid,
          isValidating,
          setFieldValue,
          submitForm,
          values: { acceptedTerms, companyName, firstName, isCompany, lastName, password },
        } = props

        const { strength, strengthMsg } = calcPasswordStrength(password)
        const strongPassword = strength >= 75

        const formStatus = isValid && !isValidating && !isSubmitting && strongPassword
        const isValidToSubmit = formStatus && acceptedTerms && !!isCurrentResident

        return (
          <Form>
            <Header />
            <div className="container flex">
              <div className="md:w-2/3 xs:w-full pr-0 md:pr-20 md:border-r-2 border-red-100">
                <Heading size="h6" className="mb-1">
                  CREATE YOUR ACCOUNT
                </Heading>
                <CurrentResident {...{ isCurrentResident, handleChange: setIsCurrentResident }} />
                <Business {...{ isCompany, isCurrentResident, setFieldValue }} />
                {isLinkedInAuthEnabled && <LinkedInCreateAccount {...{ companyName, isCompany, isCurrentResident }} />}
                <User
                  {...{
                    isCurrentResident,
                    isFurloughReferral,
                    password,
                    setFieldValue,
                    strength,
                    strengthMsg,
                    isCustomReferral,
                  }}
                />
                <TermsOfUse {...{ companyName, firstName, isCompany, lastName }} />
                <Button
                  label="Accept and Continue Registration"
                  id="continueRegistrationButton"
                  color="primary"
                  onClick={submitForm}
                  className={`w-full ${!isValidToSubmit ? 'cursor-not-allowed' : ''}`}
                  disabled={!isValidToSubmit}
                  isLoading={isLoading}
                />
              </div>
              <div>
                <Benefits />
              </div>
            </div>
            <div style={{ paddingTop: '2.5%' }} />
            <ApplicationInfoModal {...{ isCurrentResident, handleChange: setIsCurrentResident }} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default CreateAccount
